import React, { useId, useMemo } from "react";
import { CohortData } from "../../../types/unit-economics.types";
import { getDeltaDisplay } from "./deltaDisplay";

interface CohortChartProps {
  cohorts: CohortData[];
  /** Formats a grosze value for axis labels, tooltips and the data table. */
  formatFn: (cents: number) => string;
}

// Below this size a cohort is statistically unstable → dashed line + legend.
const UNSTABLE_N = 5;
// Below this size a single tenant could be identified → line hidden entirely.
const HIDE_N = 3;

const CHART_W = 640;
const CHART_H = 280;
const PAD_LEFT = 56;
const PAD_RIGHT = 16;
const PAD_TOP = 16;
const PAD_BOTTOM = 32;

// Colour-blind-friendly qualitative palette; cycled if there are more cohorts.
const PALETTE = [
  "#2563eb", // blue-600
  "#16a34a", // green-600
  "#d97706", // amber-600
  "#9333ea", // purple-600
  "#dc2626", // red-600
  "#0891b2", // cyan-600
  "#ca8a04", // yellow-600
  "#4f46e5", // indigo-600
];

const formatCohortMonth = (month: string): string => {
  const [y, m] = month.split("-");
  return `${m}.${y}`;
};

interface PlottedCohort {
  cohort: CohortData;
  color: string;
  visible: boolean;
  unstable: boolean;
  points: { age: number; value: number }[];
}

/**
 * Last age index where BOTH cohorts have a non-null value — the "full age N"
 * used for the headline comparison so we compare like-for-like maturity.
 */
const lastSharedAge = (a: CohortData, b: CohortData): number | null => {
  const len = Math.min(a.revenueByAgeMonth.length, b.revenueByAgeMonth.length);
  for (let i = len - 1; i >= 0; i--) {
    if (a.revenueByAgeMonth[i] !== null && b.revenueByAgeMonth[i] !== null) {
      return i;
    }
  }
  return null;
};

/**
 * Cumulative-revenue-per-customer by cohort age. Each line is a cohort (grouped
 * by month of first positive payment); the x axis is age in months, y is
 * cumulative revenue per customer. Newer lines sitting above older ones means
 * LTV is improving. Small cohorts are de-emphasised: `n < 5` renders dashed
 * (with a legend note), `n < 3` is hidden entirely (single-tenant privacy, M3).
 * An equivalent data table is provided for screen readers (L4).
 */
const CohortChart: React.FC<CohortChartProps> = ({ cohorts, formatFn }) => {
  const titleId = useId();

  const sorted = useMemo(
    () => [...cohorts].sort((a, b) => a.cohortMonth.localeCompare(b.cohortMonth)),
    [cohorts],
  );

  const plotted = useMemo<PlottedCohort[]>(
    () =>
      sorted.map((cohort, i) => {
        const visible = cohort.n >= HIDE_N;
        const points = cohort.revenueByAgeMonth
          .map((value, age) => ({ value, age }))
          .filter(
            (p): p is { value: number; age: number } => p.value !== null,
          );
        return {
          cohort,
          color: PALETTE[i % PALETTE.length],
          visible,
          unstable: cohort.n < UNSTABLE_N,
          points,
        };
      }),
    [sorted],
  );

  const { maxAge, maxValue } = useMemo(() => {
    let age = 0;
    let value = 0;
    for (const p of plotted) {
      if (!p.visible) continue;
      for (const pt of p.points) {
        if (pt.age > age) age = pt.age;
        if (pt.value > value) value = pt.value;
      }
    }
    return { maxAge: age, maxValue: value };
  }, [plotted]);

  const hasVisibleData = plotted.some((p) => p.visible && p.points.length > 0);
  const hasUnstable = plotted.some((p) => p.visible && p.unstable);

  // Headline: newest vs previous cohort at their last shared full age.
  const headline = useMemo(() => {
    const visibleCohorts = sorted.filter((c) => c.n >= HIDE_N);
    if (visibleCohorts.length < 2) return null;
    const newest = visibleCohorts[visibleCohorts.length - 1];
    const previous = visibleCohorts[visibleCohorts.length - 2];
    const age = lastSharedAge(newest, previous);
    if (age === null) return null;
    const newestVal = newest.revenueByAgeMonth[age];
    const prevVal = previous.revenueByAgeMonth[age];
    if (newestVal === null || prevVal === null) return null;
    return {
      age,
      newest,
      previous,
      newestVal,
      prevVal,
      delta: getDeltaDisplay(newestVal, prevVal),
    };
  }, [sorted]);

  const innerW = CHART_W - PAD_LEFT - PAD_RIGHT;
  const innerH = CHART_H - PAD_TOP - PAD_BOTTOM;
  const xFor = (age: number): number =>
    PAD_LEFT + (maxAge === 0 ? 0 : (age / maxAge) * innerW);
  const yFor = (value: number): number =>
    PAD_TOP + innerH - (maxValue === 0 ? 0 : (value / maxValue) * innerH);

  if (!hasVisibleData) {
    return (
      <p className="rounded-lg border border-neutral-200 bg-white p-8 text-center text-sm text-neutral-500">
        Brak wystarczających danych kohortowych do wyświetlenia wykresu.
      </p>
    );
  }

  // 4 horizontal gridlines + axis labels.
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((f) => ({
    value: maxValue * f,
    y: yFor(maxValue * f),
  }));

  const ariaLabel =
    "Wykres kohortowy: skumulowany przychód na klienta według wieku kohorty w miesiącach. " +
    (headline
      ? `Najnowsza kohorta ${formatCohortMonth(
          headline.newest.cohortMonth,
        )} w wieku ${headline.age} mies. osiąga ${formatFn(
          headline.newestVal,
        )} wobec ${formatFn(headline.prevVal)} poprzedniej kohorty.`
      : "");

  return (
    <div className="space-y-4">
      {headline && (
        <div className="rounded-lg border border-neutral-200 bg-white p-4 text-sm">
          <span className="text-neutral-600">
            Najnowsza kohorta (
            <span className="font-medium text-neutral-800">
              {formatCohortMonth(headline.newest.cohortMonth)}
            </span>
            ) w wieku {headline.age} mies.:{" "}
          </span>
          <span className="font-semibold text-neutral-900">
            {formatFn(headline.newestVal)}
          </span>
          <span className={`ml-2 font-medium ${headline.delta.colorClass}`}>
            {headline.delta.arrow && (
              <span aria-hidden="true" className="mr-0.5">
                {headline.delta.arrow}
              </span>
            )}
            {headline.delta.text}
          </span>
          <span className="text-neutral-500">
            {" "}
            vs {formatFn(headline.prevVal)} (
            {formatCohortMonth(headline.previous.cohortMonth)})
          </span>
        </div>
      )}

      <div className="rounded-lg border border-neutral-200 bg-white p-4">
        <svg
          role="img"
          aria-label={ariaLabel}
          aria-describedby={titleId}
          viewBox={`0 0 ${CHART_W} ${CHART_H}`}
          className="w-full"
        >
          <title id={titleId}>{ariaLabel}</title>

          {/* Y gridlines + labels */}
          {yTicks.map((tick, i) => (
            <g key={i}>
              <line
                x1={PAD_LEFT}
                y1={tick.y}
                x2={CHART_W - PAD_RIGHT}
                y2={tick.y}
                stroke="rgb(229 229 229)"
                strokeWidth={1}
              />
              <text
                x={PAD_LEFT - 6}
                y={tick.y + 3}
                textAnchor="end"
                className="fill-neutral-400 text-[10px]"
              >
                {formatFn(tick.value)}
              </text>
            </g>
          ))}

          {/* X axis labels (age in months) */}
          {Array.from({ length: maxAge + 1 }, (_, age) => age)
            .filter(
              (age) => maxAge <= 12 || age % Math.ceil((maxAge + 1) / 12) === 0,
            )
            .map((age) => (
              <text
                key={age}
                x={xFor(age)}
                y={CHART_H - PAD_BOTTOM + 16}
                textAnchor="middle"
                className="fill-neutral-400 text-[10px]"
              >
                {age}
              </text>
            ))}
          <text
            x={PAD_LEFT + innerW / 2}
            y={CHART_H - 2}
            textAnchor="middle"
            className="fill-neutral-500 text-[10px]"
          >
            Wiek kohorty (miesiące)
          </text>

          {/* Cohort lines */}
          {plotted
            .filter((p) => p.visible && p.points.length > 0)
            .map((p) => {
              const path = p.points
                .map(
                  (pt, i) =>
                    `${i === 0 ? "M" : "L"}${xFor(pt.age).toFixed(2)},${yFor(
                      pt.value,
                    ).toFixed(2)}`,
                )
                .join(" ");
              return (
                <g key={p.cohort.cohortMonth}>
                  <path
                    d={path}
                    fill="none"
                    stroke={p.color}
                    strokeWidth={1.75}
                    strokeDasharray={p.unstable ? "4 3" : undefined}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  {p.points.map((pt) => (
                    <circle
                      key={pt.age}
                      cx={xFor(pt.age)}
                      cy={yFor(pt.value)}
                      r={2}
                      fill={p.color}
                    >
                      <title>{`${formatCohortMonth(
                        p.cohort.cohortMonth,
                      )} · wiek ${pt.age} mies. · ${formatFn(pt.value)}`}</title>
                    </circle>
                  ))}
                </g>
              );
            })}
        </svg>

        {/* Legend */}
        <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 text-xs">
          {plotted
            .filter((p) => p.visible && p.points.length > 0)
            .map((p) => (
              <li
                key={p.cohort.cohortMonth}
                className="flex items-center gap-1.5 text-neutral-600"
              >
                <span
                  aria-hidden="true"
                  className="inline-block h-2.5 w-4 rounded-sm"
                  style={
                    p.unstable
                      ? {
                          backgroundImage: `repeating-linear-gradient(90deg, ${p.color} 0 4px, transparent 4px 7px)`,
                        }
                      : { backgroundColor: p.color }
                  }
                />
                {formatCohortMonth(p.cohort.cohortMonth)} (N={p.cohort.n})
                {p.unstable && (
                  <span className="text-neutral-400"> · niestabilna</span>
                )}
              </li>
            ))}
        </ul>

        {hasUnstable && (
          <p className="mt-2 text-xs text-neutral-400">
            Kohorty z N&lt;5 (linia przerywana) mogą być statystycznie
            niestabilne. Kohorty z N&lt;3 są ukryte ze względu na prywatność.
          </p>
        )}
      </div>

      {/* Screen-reader / fallback data table (L4) */}
      <table className="sr-only">
        <caption>
          Skumulowany przychód na klienta według wieku kohorty (miesiące)
        </caption>
        <thead>
          <tr>
            <th scope="col">Kohorta</th>
            <th scope="col">Rozmiar (N)</th>
            <th scope="col">Wartości wg wieku (mies.)</th>
          </tr>
        </thead>
        <tbody>
          {plotted
            .filter((p) => p.visible)
            .map((p) => (
              <tr key={p.cohort.cohortMonth}>
                <th scope="row">{formatCohortMonth(p.cohort.cohortMonth)}</th>
                <td>{p.cohort.n}</td>
                <td>
                  {p.points
                    .map(
                      (pt) => `wiek ${pt.age}: ${formatFn(pt.value)}`,
                    )
                    .join("; ")}
                </td>
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
};

export default CohortChart;
